// src/components/LandingPage.jsx

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LocalShipping as LocalShippingIcon,
  Security as SecurityIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import {
  fetchFeaturedProducts,
  fetchPromotions,
  fetchRecommendedForYou,
} from "../api/homePageProductsapi";
import { getUserProfile } from "../utils/auth";
import image from "../images/image.jpg";
import imagesDashboard from "../images/ImagesDashBoard2.jpg";
import imagesDashboard3 from "../images/ImagesDashBoard3.png";
import FashionImage1 from "../images/fashion-stylish-model-dressed-elegant-light-pink-suit.jpg";
import FashionImage2 from "../images/Jewelry-laydown-two.jpg";
import FashionImage3 from "../images/left-1.jpg";
import { useNavigate } from "react-router-dom";
import { useChatProducts } from "../context/ChatProductsContext";
import WishlistToggle from "./WishlistToggle";

// Helper to safely get image URL — returns null if invalid
const getImageUrl = (rawUrl) => {
  if (!rawUrl) return null;
  const cleaned = rawUrl
    .trim()
    .replace("storage.cloud.google.com", "storage.googleapis.com");
  return cleaned || null;
};

// Hero images data - moved outside component to prevent recreation on every render
const heroImages = [
  {
    src: image,
    alt: "Hero Fashion",
    top_label: "New Season",
    Title: "Discover Your Perfect Style",
    Buttom_label:
      "Exclusive collection of premium fashion for the modern lifestyle",
    Total_Button: 2,
    ButtonsName: [{ name: "Shop Women" }, { name: "Shop Men" }],
  },
  {
    src: imagesDashboard,
    alt: "Dashboard",
    top_label: "Trending Now",
    Title: "Elegant Fashion For Every Occasion",
    Buttom_label: "Sophisticated designs that redefine modern elegance",
    Total_Button: 2,
    ButtonsName: [{ name: "Shop collections" }, { name: "View Lookbook" }],
  },
  {
    src: imagesDashboard3,
    alt: "Dashboard",
    top_label: "Limited Edition",
    Title: "Luxury Meets Comfort",
    Buttom_label: "Premium quality pieces designed for your lifestyle",
    Total_Button: 2,
    ButtonsName: [{ name: "Discover More" }, { name: "New Arrivals" }],
  },
];

const Sectiontwoimages = [
  {
    src: FashionImage3,
    Title: "Women",
    SubTitle: "250+ Items",
    ButtonName: "Shop Now",
  },
  {
    src: FashionImage1,
    Title: "Men",
    SubTitle: "180+ Items",
    ButtonName: "Shop Now",
    disabled: true,
  },
  {
    src: FashionImage2,
    Title: "Accessories",
    SubTitle: "120+ Items",
    ButtonName: "Shop Now",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { chatProducts } = useChatProducts();

  // Use state for customerId to react to auth changes
  const [customerId, setCustomerId] = useState(() => {
    const userProfile = getUserProfile();
    return userProfile?.customer_id || null;
  });

  // Listen for auth changes (e.g., after login)
  useEffect(() => {
    const updateCustomerId = () => {
      const userProfile = getUserProfile();
      const newCustomerId = userProfile?.customer_id || null;
      setCustomerId((prev) => {
        if (prev !== newCustomerId) {
          return newCustomerId;
        }
        return prev;
      });
    };

    // Check immediately
    updateCustomerId();

    // Listen for auth state change events
    const handleAuthChange = (event) => {
      // Update customerId when auth changes
      setTimeout(updateCustomerId, 100); // Small delay to ensure cookies are set
    };

    window.addEventListener("authStateChanged", handleAuthChange);

    // Also poll periodically as a fallback (in case event doesn't fire)
    const intervalId = setInterval(updateCustomerId, 1000);

    return () => {
      window.removeEventListener("authStateChanged", handleAuthChange);
      clearInterval(intervalId);
    };
  }, []);

  const handleProductClick = useCallback(
    (product) => {
      if (!product?.id) return;

      // Check if user is logged in
      const userProfile = getUserProfile();
      const customerId = userProfile?.customer_id;

      if (!customerId) {
        // Store product ID and open login modal
        sessionStorage.setItem("pendingProductId", product.id);
        window.dispatchEvent(
          new CustomEvent("openLoginModal", {
            detail: { productId: product.id },
          })
        );
        return;
      }

      // User is logged in, navigate to product
      navigate(`/item/${product.id}`);
    },
    [navigate]
  );

  // Guest state
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 8;

  // Logged-in state
  const [promotions, setPromotions] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loadingPersonalized, setLoadingPersonalized] = useState(false);

  // Hero carousel state
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch featured (guests)
  const loadFeatured = useCallback(
    async (page) => {
      setLoadingFeatured(true);
      try {
        const data = await fetchFeaturedProducts(page, perPage);
        setFeaturedProducts(
          data.records.map((r) => ({
            id: r.article_id,
            enhanced_name: r.enhanced_prod_name || r.prod_name || "Product",
            enhanced_description:
              r.enhanced_description || r.detail_desc || "No description",
            price: parseFloat(r.price) || 0,
            image: getImageUrl(r.image_url || r.image_uri),
            colour: r.colour_group_name || "Unknown",
          }))
        );
        setTotalPages(data.pages);
      } catch (err) {
        console.error("Error loading featured products:", err);
      } finally {
        setLoadingFeatured(false);
      }
    },
    [perPage]
  );

  // Fetch personalized (logged-in)
  const loadPersonalized = useCallback(async () => {
    if (!customerId) return;

    setLoadingPersonalized(true);
    try {
      const [promoData, recData] = await Promise.all([
        fetchPromotions(customerId),
        fetchRecommendedForYou(customerId),
      ]);

      setPromotions(
        promoData.map((item) => ({
          id: item.article_id,
          enhanced_name: item.enhanced_prod_name || item.prod_name || "Product",
          enhanced_description:
            item.enhanced_description || item.detail_desc || "No description",
          price: parseFloat(item.original_price) || parseFloat(item.price) || 0,
          discounted_price:
            parseFloat(item.discounted_price) || parseFloat(item.price) || 0,
          discount_percent: item.discount_percent || 0,
          image: getImageUrl(item.image_url || item.image_uri),
          department: item.department_name || "Unknown",
        }))
      );
      setRecommended(
        recData.map((item) => ({
          id: item.article_id,
          enhanced_name: item.enhanced_prod_name || item.prod_name || "Product",
          enhanced_description:
            item.enhanced_description || item.detail_desc || "No description",
          price: parseFloat(item.price) || 0,
          image: getImageUrl(item.image_url || item.image_uri),
          colour: item.colour_group_name || "Unknown",
          rating: item.rating || 4.5,
        }))
      );
    } catch (err) {
      console.error("Error loading personalized products:", err);
    } finally {
      setLoadingPersonalized(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) {
      loadFeatured(currentPage);
    }
  }, [currentPage, customerId, loadFeatured]);

  useEffect(() => {
    if (customerId) {
      loadPersonalized();
    }
  }, [customerId, loadPersonalized]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  }, [currentPage, totalPages]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  }, [currentPage]);

  // Hero carousel navigation - memoized to prevent recreation
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Auto-rotate carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 10000); // 5 seconds

    return () => clearInterval(interval);
  }, [nextSlide]);

  // Loading
  if ((loadingFeatured && !customerId) || (loadingPersonalized && customerId)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Hero Carousel - Full Width */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 0, md: "500px" },
          width: "100%",
          mt: { xs: 0 },
          display: { xs: "none", md: "block" },
          overflow: "hidden",
        }}
      >
        {/* Images Container */}
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          <AnimatePresence mode="wait">
            {heroImages[currentSlide] && (
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={heroImages[currentSlide].src}
                  alt={heroImages[currentSlide].alt}
                  loading="eager"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center center",
                    display: "block",
                    userSelect: "none",
                    // Apply a dark overlay for text contrast
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.4))",
                      pointerEvents: "none",
                    },
                  }}
                />
                {/* Text Overlays */}
                {heroImages[currentSlide].Title && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      zIndex: 5,
                    }}
                  >
                    <Container maxWidth="xl">
                      <Box
                        sx={{
                          ml: { xs: 2, md: 3, lg: 4, xl: 6 },
                          maxWidth: "600px",
                        }}
                      >
                        {/* Top Label - "Trending Now" */}
                        {heroImages[currentSlide].top_label && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            <Chip
                              label={heroImages[currentSlide].top_label}
                              sx={{
                                mb: 2,
                                bgcolor: "rgba(255, 255, 255, 0.8)",
                                color: "black",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                borderRadius: "4px",
                                border: "1px solid rgba(0,0,0,0.1)",
                                boxShadow: 1,
                              }}
                            />
                          </motion.div>
                        )}
                        {/* Main Title */}
                        {heroImages[currentSlide].Title && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            <Typography
                              variant="h1"
                              sx={{
                                fontSize: {
                                  xs: "2rem",
                                  md: "2.5rem", // Reduced from 3rem to match Figma
                                  lg: "3rem", // Reduced from 4rem
                                  xl: "3.5rem", // Reduced from 5rem
                                },
                                fontWeight: 700,
                                color: "white",
                                mb: 2,
                                lineHeight: 1.2,
                                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                              }}
                            >
                              {heroImages[currentSlide].Title}
                            </Typography>
                          </motion.div>
                        )}
                        {/* Subtitle */}
                        {heroImages[currentSlide].Buttom_label && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: "white",
                                fontSize: {
                                  xs: "1rem",
                                  md: "1.125rem",
                                  lg: "1.25rem",
                                },
                                mb: 3,
                                opacity: 0.95,
                                maxWidth: "600px",
                                textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                              }}
                            >
                              {heroImages[currentSlide].Buttom_label}
                            </Typography>
                          </motion.div>
                        )}
                        {/* Buttons */}
                        {heroImages[currentSlide].ButtonsName &&
                          heroImages[currentSlide].ButtonsName.length > 0 && (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.6 }}
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                flexWrap="wrap"
                              >
                                {heroImages[currentSlide].ButtonsName.map(
                                  (btn, btnIndex) => (
                                    <motion.div
                                      key={btnIndex}
                                      whileHover={{ scale: 1.05, y: -2 }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <Button
                                        variant={
                                          btnIndex === 0
                                            ? "contained"
                                            : "outlined"
                                        }
                                        sx={{
                                          borderRadius: "4px", // Sharp corners
                                          px: 2,
                                          py: 1,
                                          fontSize: {
                                            xs: "0.875rem",
                                            md: "1rem",
                                          },
                                          fontWeight: 500,
                                          boxShadow: 0, // No shadow
                                          border: "1px solid white", // White border for outlined
                                          color:
                                            btnIndex === 0 ? "black" : "white",
                                          bgcolor:
                                            btnIndex === 0
                                              ? "white"
                                              : "transparent",
                                          "&:hover": {
                                            bgcolor:
                                              btnIndex === 0
                                                ? "grey.100"
                                                : "rgba(255,255,255,0.1)",
                                            border: "1px solid white",
                                          },
                                          "& .MuiButton-endIcon": {
                                            marginLeft: "4px",
                                          },
                                        }}
                                      >
                                        {btn.name}
                                      </Button>
                                    </motion.div>
                                  )
                                )}
                              </Stack>
                            </motion.div>
                          )}
                      </Box>
                    </Container>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Navigation Buttons - Small & Subtle */}
        {heroImages.length > 1 && (
          <>
            {/* Left Arrow */}
            <Tooltip title="Previous" placement="top">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 100,
                }}
              >
                <IconButton
                  onClick={prevSlide}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                    width: 36,
                    height: 36,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: "white",
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </motion.div>
            </Tooltip>
            {/* Right Arrow */}
            <Tooltip title="Next" placement="top">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 100,
                }}
              >
                <IconButton
                  onClick={nextSlide}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                    width: 36,
                    height: 36,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: "white",
                    },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </motion.div>
            </Tooltip>
            {/* Pagination Dots */}
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 100,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1}>
                {heroImages.map((_, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      onClick={() => goToSlide(index)}
                      sx={{
                        p: 0,
                        width: index === currentSlide ? 12 : 8,
                        height: 8,
                        borderRadius: "4px",
                        bgcolor:
                          index === currentSlide
                            ? "white"
                            : "rgba(255, 255, 255, 0.5)",
                        boxShadow: index === currentSlide ? 1 : 0,
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.75)",
                        },
                      }}
                    />
                  </motion.div>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Box>
      {/* Features Section */}
      <Box
        sx={{
          backgroundColor: "#EBE8E6",
          py: 3,
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="center"
          >
            {/* Free Shipping */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: { xs: "center", sm: "center" },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "black",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LocalShippingIcon sx={{ fontSize: 30, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.5,
                      fontSize: "0.95rem",
                    }}
                  >
                    Free Shipping
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    On orders over $100
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Secure Payment */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: { xs: "center", sm: "center" },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "black",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 30, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.5,
                      fontSize: "0.95rem",
                    }}
                  >
                    Secure Payment
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    100% secure transactions
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* 24/7 Support */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: { xs: "center", sm: "center" },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "black",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 30, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.5,
                      fontSize: "0.95rem",
                    }}
                  >
                    24/7 Support
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    Dedicated customer service
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Shop by Category Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 1,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            Shop by Category
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            Explore our collections
          </Typography>
        </Box>

        {/* Category Cards */}
        <Grid container spacing={3}>
          {Sectiontwoimages.map((category, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                onClick={() => {
                  if (!category.disabled) {
                    // If Women category, open the Women menu in topbar
                    if (category.Title.toLowerCase() === "women") {
                      window.dispatchEvent(new CustomEvent("openWomenMenu"));
                    } else if (category.Title.toLowerCase() === "accessories") {
                      // If Accessories category, open the Accessories menu in topbar
                      window.dispatchEvent(new CustomEvent("openAccessoriesMenu"));
                    } else {
                      // Navigate to category page for other categories
                      navigate(
                        `/products?category=${category.Title.toLowerCase()}`
                      );
                    }
                  }
                }}
                sx={{
                  position: "relative",
                  height: "500px",
                  minHeight: "500px",
                  overflow: "hidden",
                  cursor: "pointer",
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundImage: `url(${category.src})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  width: "100%",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                    backgroundSize: "110%",
                  },
                  transition: "all 0.4s ease",
                }}
              >
                {/* Gradient Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    zIndex: 1,
                  }}
                >
                  {/* Category Title with background for Accessories */}
                  <Box
                    sx={{
                      mb: 0.5,
                      ...{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "white",
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    >
                      {category.Title}
                    </Typography>
                  </Box>
                  {/* Item Count */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "white",
                      mb: 2,
                      fontSize: "0.9rem",
                    }}
                  >
                    {category.SubTitle}
                  </Typography>
                  {/* Shop Now Button */}
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={category.disabled}
                    sx={{
                      bgcolor: "white",
                      color: "text.primary",
                      fontWeight: 600,
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      boxShadow: 2,
                      "&:hover": {
                        bgcolor: "grey.100",
                        transform: "translateX(4px)",
                        boxShadow: 4,
                      },
                      "&:disabled": {
                        bgcolor: "grey.300",
                        color: "grey.500",
                        cursor: "not-allowed",
                        transform: "none",
                        boxShadow: 1,
                      },
                      transition: "all 0.2s ease",
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click from firing
                      if (!category.disabled) {
                        // If Women category, open the Women menu in topbar
                        if (category.Title.toLowerCase() === "women") {
                          window.dispatchEvent(new CustomEvent("openWomenMenu"));
                        } else if (category.Title.toLowerCase() === "accessories") {
                          // If Accessories category, open the Accessories menu in topbar
                          window.dispatchEvent(new CustomEvent("openAccessoriesMenu"));
                        } else {
                          // Navigate to category page for other categories
                          navigate(
                            `/products?category=${category.Title.toLowerCase()}`
                          );
                        }
                      }
                    }}
                  >
                    {category.ButtonName}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* LOGGED-IN: Promotions + Recommended */}
        {customerId && (
          <>
            {recommended.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, color: "text.primary", mb: 3 }}
                  >
                    Recommended For You
                  </Typography>
                </motion.div>
                <Grid container spacing={3}>
                  {/* Products coming from backend "recommended" API */}
                  {recommended.map((product, idx) => (
                    <Grid item xs={12} sm={6} lg={3} key={product.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: (chatProducts.length + idx) * 0.1,
                        }}
                        whileHover={{ y: -8, scale: 1.02 }}
                      >
                        <Card
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: 2,
                            "&:hover": {
                              boxShadow: 8,
                            },
                            transition: "all 0.3s",
                          }}
                          onClick={() => handleProductClick(product)}
                        >
                          {product.image && (
                            <CardMedia
                              component="img"
                              height="256"
                              image={product.image}
                              alt={product.enhanced_name}
                              sx={{
                                objectFit: "cover",
                                transition: "transform 0.3s",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                },
                              }}
                            />
                          )}
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start", // allows title to wrap while heart stays top-aligned
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: "text.primary",
                                  flex: 1,
                                  mr: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  "&:hover": {
                                    color: "primary.main",
                                  },
                                }}
                              >
                                {product.enhanced_name}
                              </Typography>
                              {/* ✅ Wishlist Toggle — inline, right-aligned */}
                              <Box sx={{ flexShrink: 0, mt: "-2px" }}>
                                <WishlistToggle product={product} />
                              </Box>
                            </Box>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {product.enhanced_description}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 2,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  background:
                                    "linear-gradient(to right, #22d3ee, #60a5fa, #38bdf8)",
                                  backgroundClip: "text",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  fontWeight: 700,
                                }}
                              >
                                ${product.price.toFixed(2)}
                              </Typography>
                              <Chip
                                label={product.colour}
                                size="small"
                                sx={{
                                  background:
                                    "linear-gradient(to right, #cffafe, #dbeafe, #e0f2fe)",
                                  color: "text.primary",
                                  border: "1px solid",
                                  borderColor: "primary.light",
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {promotions.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, color: "text.primary", mb: 3 }}
                  >
                    Special Promotions
                  </Typography>
                </motion.div>
                <Grid container spacing={3}>
                  {promotions.map((product, idx) => (
                    <Grid item xs={12} md={4} key={product.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ y: -8, scale: 1.02 }}
                      >
                        <Card
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            boxShadow: 2,
                            "&:hover": {
                              boxShadow: 8,
                            },
                            transition: "all 0.3s",
                          }}
                          onClick={() => handleProductClick(product)}
                        >
                          {product.discount_percent > 0 && (
                            <motion.div
                              initial={{ scale: 0, rotate: -180 }}
                              animate={{ scale: 1, rotate: 0 }}
                              transition={{ delay: 0.2 + idx * 0.1 }}
                              style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                zIndex: 10,
                              }}
                            >
                              <Chip
                                label={`${product.discount_percent}% OFF`}
                                sx={{
                                  background:
                                    "linear-gradient(to right, #ef4444, #dc2626)",
                                  color: "white",
                                  fontWeight: 700,
                                  boxShadow: 3,
                                }}
                              />
                            </motion.div>
                          )}
                          {product.image && (
                            <CardMedia
                              component="img"
                              height="256"
                              image={product.image}
                              alt={product.enhanced_name}
                              sx={{
                                objectFit: "cover",
                                transition: "transform 0.3s",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                },
                              }}
                            />
                          )}
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start", // allows title to wrap while heart stays top-aligned
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: "text.primary",
                                  flex: 1,
                                  mr: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  "&:hover": {
                                    color: "primary.main",
                                  },
                                }}
                              >
                                {product.enhanced_name}
                              </Typography>
                              {/* ✅ Wishlist Toggle — inline, right-aligned */}
                              <Box sx={{ flexShrink: 0, mt: "-2px" }}>
                                <WishlistToggle product={product} />
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {product.enhanced_description}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mt: 2 }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  background:
                                    "linear-gradient(to right, #ef4444, #dc2626)",
                                  backgroundClip: "text",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  fontWeight: 700,
                                }}
                              >
                                ${product.discounted_price.toFixed(2)}
                              </Typography>
                              {product.discounted_price < product.price && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.disabled",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  ${product.price.toFixed(2)}
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}

        {/* GUEST: Only Featured Products */}
        {!customerId && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  Featured Products
                </Typography>
              </motion.div>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Previous Page">
                  <motion.div
                    whileHover={{ scale: currentPage === 1 ? 1 : 1.1, x: -2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      onClick={handlePrev}
                      disabled={currentPage === 1}
                      sx={{
                        bgcolor: "rgba(51, 65, 85, 0.8)",
                        background:
                          "linear-gradient(to bottom, rgba(71, 85, 105, 0.9), rgba(51, 65, 85, 0.8))",
                        color: "white",
                        boxShadow:
                          "0 4px 12px rgba(0, 0, 0, 0.3), 0 0 8px rgba(51, 65, 85, 0.4)",
                        width: 40,
                        height: 40,
                        backdropFilter: "blur(8px)",
                        "&:hover": {
                          bgcolor: "rgba(51, 65, 85, 0.95)",
                          background:
                            "linear-gradient(to bottom, rgba(71, 85, 105, 1), rgba(51, 65, 85, 0.95))",
                          boxShadow:
                            "0 6px 16px rgba(0, 0, 0, 0.4), 0 0 12px rgba(51, 65, 85, 0.5)",
                        },
                        "&.Mui-disabled": {
                          background: "rgba(156, 163, 175, 0.5)",
                          color: "rgba(107, 114, 128, 0.7)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </motion.div>
                </Tooltip>
                <motion.div
                  key={currentPage}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Chip
                    label={`Page ${currentPage} of ${totalPages}`}
                    sx={{
                      bgcolor: "white",
                      border: "1px solid",
                      borderColor: "grey.300",
                      boxShadow: 2,
                      fontWeight: 500,
                    }}
                  />
                </motion.div>
                <Tooltip title="Next Page">
                  <motion.div
                    whileHover={{
                      scale: currentPage === totalPages ? 1 : 1.1,
                      x: 2,
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      sx={{
                        bgcolor: "rgba(51, 65, 85, 0.8)",
                        background:
                          "linear-gradient(to bottom, rgba(71, 85, 105, 0.9), rgba(51, 65, 85, 0.8))",
                        color: "white",
                        boxShadow:
                          "0 4px 12px rgba(0, 0, 0, 0.3), 0 0 8px rgba(51, 65, 85, 0.4)",
                        width: 40,
                        height: 40,
                        backdropFilter: "blur(8px)",
                        "&:hover": {
                          bgcolor: "rgba(51, 65, 85, 0.95)",
                          background:
                            "linear-gradient(to bottom, rgba(71, 85, 105, 1), rgba(51, 65, 85, 0.95))",
                          boxShadow:
                            "0 6px 16px rgba(0, 0, 0, 0.4), 0 0 12px rgba(51, 65, 85, 0.5)",
                        },
                        "&.Mui-disabled": {
                          background: "rgba(156, 163, 175, 0.5)",
                          color: "rgba(107, 114, 128, 0.7)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </motion.div>
                </Tooltip>
              </Stack>
            </Box>

            <Grid container spacing={3}>
              {featuredProducts.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No products available.
                  </Typography>
                </Grid>
              ) : (
                featuredProducts.map((product, idx) => (
                  <Grid item xs={12} sm={6} lg={3} key={product.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ y: -8, scale: 1.02 }}
                    >
                      <Card
                        sx={{
                          cursor: "pointer",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          boxShadow: 2,
                          "&:hover": {
                            boxShadow: 8,
                          },
                          transition: "all 0.3s",
                        }}
                        onClick={() => handleProductClick(product)}
                      >
                        {product.image && (
                          <CardMedia
                            component="img"
                            height="256"
                            image={product.image}
                            alt={product.enhanced_name}
                            sx={{
                              objectFit: "cover",
                              transition: "transform 0.3s",
                              "&:hover": {
                                transform: "scale(1.1)",
                              },
                            }}
                          />
                        )}
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start", // allows title to wrap while heart stays top-aligned
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "text.primary",
                                flex: 1,
                                mr: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                "&:hover": {
                                  color: "primary.main",
                                },
                              }}
                            >
                              {product.enhanced_name}
                            </Typography>
                            {/* ✅ Wishlist Toggle — inline, right-aligned */}
                            <Box sx={{ flexShrink: 0, mt: "-2px" }}>
                              <WishlistToggle product={product} />
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {product.enhanced_description}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                background:
                                  "linear-gradient(to right, #22d3ee, #60a5fa, #38bdf8)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                fontWeight: 700,
                              }}
                            >
                              ${product.price.toFixed(2)}
                            </Typography>
                            <Chip
                              label={product.colour}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(to right, #cffafe, #dbeafe, #e0f2fe)",
                                color: "text.primary",
                                border: "1px solid",
                                borderColor: "primary.light",
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}
      </Container>
    </>
  );
}
